/** @jsxRuntime classic */
/** @jsx jsx */
import {css, jsx} from "@emotion/core";
import React, {Fragment} from 'react';
import {FieldDef, GeoFieldType, LayerDef, LayerType} from "../types";
import {Collapse, FormGroup, ListItem, Select, Slider, Switch, TextInput} from "@ice/components";
import {getMapComponent} from "../store/Accessor";
import {IconUtil, PointShapeInfo} from "../util/IconUtil";
import {XIcon} from "../ui/XIcon";
import ColorPaletteChooser from "./palettes/ColorPaletteChooser";
import {Color, ColorMap} from "../util/Color";
import {$t} from "../lang/LangUtil";
import {DatasetFieldSelect} from "./DatasetFieldSelect";
import {FormContainer} from "./FormContainer";
import {ColorUtils} from "../util/ColorUtils";
import {ColorMapSelection} from "./palettes/ColorMapSelection";
import {LayerUtil} from "../map/LayerUtil";


interface LayerPanelProps {
    layerDef: LayerDef;
    isExpanded?: boolean;
}


interface LayerPanelState {
}


export default class LayerPanel extends React.Component<LayerPanelProps, LayerPanelState> {

    private keySwitcher: boolean = false;

    constructor(props: LayerPanelProps) {
        super(props);
        this.state = {
        }
    };

    private refresh = (force: boolean = false) => {
        if (force) {
            this.keySwitcher = !this.keySwitcher;
        }
        this.forceUpdate();
    }

    private onColorsChange = (colors: string[], confirmed: boolean = true) => {
        this.props.layerDef.config.colorPalette = colors.map(colAsStr => Color.createFromString(colAsStr));
        if (confirmed) {
            this.refresh();
        }
        getMapComponent().updateLayers(this.props.layerDef);
    }

    private onUseGradientToggle = () => {
        this.props.layerDef.config.useGradient = !this.props.layerDef.config.useGradient;
        this.refresh();
        getMapComponent().updateLayers(this.props.layerDef);
    }

    private onUseDirectionToggle = () => {
        this.props.layerDef.config.useHeadingField = !this.props.layerDef.config.useHeadingField;
        this.refresh();
        getMapComponent().updateLayers(this.props.layerDef);
    }

    private onOpacityChange = (opacity: number) => {
        this.props.layerDef.config.opacity = opacity;
        this.refresh();
        getMapComponent().updateExistingLayer(this.props.layerDef);
    }

    private onSizeChange = (size: number) => {
        this.props.layerDef.config.size = size;
        this.refresh();
        getMapComponent().updateLayers(this.props.layerDef);
    }

    private onSizeDependsToggle = () => {
        this.props.layerDef.config.sizeDependsOnSizeField = !this.props.layerDef.config.sizeDependsOnSizeField;
        this.refresh();
        getMapComponent().updateLayers(this.props.layerDef);
    }

    private onShapeChange = (pointShapeInfo: PointShapeInfo) => {
        this.props.layerDef.config.shape = pointShapeInfo.id;
        this.refresh();
        getMapComponent().updateLayers(this.props.layerDef);
    }

    private onLayerTypeChange = (layerType: LayerType) => {
        this.props.layerDef.type = layerType;
        this.refresh(true);
        getMapComponent().updateLayers(this.props.layerDef);
    }

    private onGeoFieldTypeChange = (geoFieldType: GeoFieldType) => {
        if (this.props.layerDef.geoFieldType !== geoFieldType) {
            this.props.layerDef.geoFieldType = geoFieldType;
            this.refresh(true);
            getMapComponent().updateLayers(this.props.layerDef);
        }
    }

    private onColorFieldChange = (fieldDef: FieldDef) => {
        this.props.layerDef.config.colorFieldId = fieldDef.id;
        this.refresh(true);
        getMapComponent().updateLayers(this.props.layerDef);
    }

    private onColorMapChange = (colorMap: ColorMap) => {
        let config = this.props.layerDef.config;
        config.colorsMapsByFields[config.colorFieldId] = colorMap;
        this.refresh(true);
        getMapComponent().updateLayers(this.props.layerDef);
    }

    private onSizeFieldChange = (fieldDef: FieldDef) => {
        this.props.layerDef.config.sizeFieldId = fieldDef.id;
        this.refresh();
        getMapComponent().updateLayers(this.props.layerDef);
    }

    private onTooltipFieldsChange = (fieldDefs: FieldDef[]) => {
        this.props.layerDef.config.tooltipFields = fieldDefs.map(f => f.id);
        this.refresh(true);
        getMapComponent().updateLayers(this.props.layerDef);
    }

    render = () => {
        const getMarks = (count: number, gap: number) =>
            Array.from(new Array(count).keys())
                .map((i) => gap * i)
                .reduce((m, tick) => ({ ...m, [tick]: tick }), {});

        const marks = getMarks(11, 10);

        const renderShapeInfoOption = (shapeInfo: PointShapeInfo) => {
            return (
                <div css={(theme) => css`
                          display: flex;
                          flex-direction: row;
                          align-items: center;
                          fill: ${theme.textColor.default};
                          margin-right: 0.4em;
                          padding: 0.25em 0;
                        `}
                >
                    <XIcon icon={shapeInfo.iconInfo} iconSize={12} css={(theme) => css`
                                              fill: ${theme.textColor.default};
                                              margin-right: 0.4em;
                                            `} />
                    <span>{shapeInfo.name}</span>
                </div>
            );
        }

        const layerDef = this.props.layerDef;
        const dataset = layerDef.dataset;

        const applicableLayerTypes: LayerType[] = ["point", "cluster"];
        if (dataset.supportsGeojson()) {
            applicableLayerTypes.push("geojson");
        }

        const geoFieldTypes: GeoFieldType[] = ["geometry", "country", "state", "county"];

        const colorsArray = layerDef.config.colorPalette.map(c => c.toHex());

        const allFields = dataset.getFieldDefs();

        const numbericFields = allFields.filter(f => f.type === "real" || f.type === "integer");

        const tooltipFields = (layerDef.config.tooltipFields || []).
                filter(id => dataset.getFieldDefById(id)).
                map((fId) => dataset.getFieldDefById(fId));

        const shapeApplicable = layerDef.type === "point";
        const sizeApplicable = layerDef.type === "point" || layerDef.type === "cluster";
        const sizeVaryApplicable = layerDef.type === "point";

        return (
            this.props.isExpanded &&
            <FormContainer style={{margin: "0 1em", overflowY: "auto"}}>
                <FormGroup inline label={$t("dataset")}>
                    <TextInput defaultValue={dataset.getName()} disabled={true} />
                </FormGroup>
                <FormGroup inline label={$t("layertype")}>
                    <Select<LayerType>
                        clearable={false}
                        searchable={false}
                        getValue={i => applicableLayerTypes[i]}
                        getOptionLabel={i => $t(applicableLayerTypes[i] as any)}
                        getSelectionLabel={def => $t(def as any)}
                        onChange={this.onLayerTypeChange}
                        optionCount={applicableLayerTypes.length}
                        value={layerDef.type}
                    />
                </FormGroup>
                {
                    layerDef.type === "geojson" &&
                    <FormGroup inline label={$t("geofield")}>
                        <Select<GeoFieldType>
                            clearable={false}
                            searchable={false}
                            getValue={i => geoFieldTypes[i]}
                            getOptionLabel={i => $t(geoFieldTypes[i] as any)}
                            getSelectionLabel={def => $t(def as any)}
                            onChange={this.onGeoFieldTypeChange}
                            optionCount={geoFieldTypes.length}
                            value={layerDef.geoFieldType}
                        />
                    </FormGroup>
                }
                <DatasetFieldSelect
                    label={$t("field")}
                    fields={allFields}
                    value={dataset.getFieldDefById(layerDef.config.colorFieldId)}
                    onFieldSelect={this.onColorFieldChange}
                />
                <FormGroup inline label={$t("tooltipfields")}>
                    <Select<FieldDef>
                        includeSelectionsInOptions={true}
                        placeholder={$t("seladdtooltipfields")}
                        clearable={true}
                        searchable={true}
                        getOptionLabel={(i) => allFields[i].name}
                        getSelectionLabel={def => def.name}
                        getValue={(i) => allFields[i]}
                        optionCount={allFields.length}
                        value={tooltipFields}
                        onChange={(selectedField) => {
                            this.onTooltipFieldsChange(selectedField as FieldDef[])
                        }}
                    />
                </FormGroup>
                {
                    layerDef.isColorFieldNumeric() &&
                    <Fragment>
                        <FormGroup inline label={$t("fillcolors")}>
                            <div>
                                <ColorPaletteChooser
                                    key={`cpc_${colorsArray}`}
                                    colors={colorsArray}
                                    isGradient={layerDef.config.useGradient}
                                    customPaletteIndex={layerDef.config.customPaletteIndex}
                                    onPaletteSelected={(colors, confirmed) => this.onColorsChange(colors, confirmed)}
                                    onCustomPaletteSelected={index => {
                                        layerDef.config.customPaletteIndex = index;
                                    }}
                                    onCustomPaletteSaved={(index, colors) => {
                                        ColorUtils.setCustomPalette(index, colors);
                                        layerDef.config.customPaletteIndex = index;
                                    }}
                                />
                            </div>
                        </FormGroup>
                        <FormGroup inline label="">
                            <div style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
                                <Switch minimal={true} checked={layerDef.config.useGradient} hideLabels inline onChange={() => this.onUseGradientToggle()} />
                                <span>{$t("usegradient")}</span>
                            </div>
                        </FormGroup>
                    </Fragment>
                }
                {
                    !layerDef.isColorFieldNumeric() &&
                    <Fragment>
                        <FormGroup inline label={$t("fillcolors")}>
                            <ColorMapSelection
                                key={`cms_${layerDef.config.colorFieldId}`}
                                keys={layerDef.dataset.getFieldDistinctValues(layerDef.config.colorFieldId)}
                                colorMap={LayerUtil.prepareColorMap(layerDef)}
                                onChanged={colorMap => {
                                    layerDef.config.colorsMapsByFields[layerDef.config.colorFieldId] = colorMap;
                                    this.onColorMapChange(colorMap);
                                }}
                            />
                        </FormGroup>
                    </Fragment>
                }
                {
                    shapeApplicable &&
                    <FormGroup inline label={$t("shape")}>
                        <Select<PointShapeInfo>
                            clearable={false}
                            searchable={false}
                            renderSelection={() => {
                                return renderShapeInfoOption(IconUtil.getPointShapeById(layerDef.config.shape))
                            }}
                            renderOption={(getProps, {optionIndex}) => {
                                const {children, ...listItemProps} = getProps();
                                const shapeInfo = IconUtil.AVAILABLE_POINT_SHAPES[optionIndex];
                                return (
                                    <ListItem onClick={listItemProps.onClick} css={(theme) => css`
                                  &:hover {
                                    background-color: ${theme.listItem.selected.backgroundColor};
                                  }`}>
                                        {renderShapeInfoOption(shapeInfo)}
                                    </ListItem>
                                );
                            }}
                            getValue={(i) => IconUtil.AVAILABLE_POINT_SHAPES[i]}
                            onChange={this.onShapeChange}
                            optionCount={IconUtil.AVAILABLE_POINT_SHAPES.length}
                            value={IconUtil.getPointShapeById(layerDef.config.shape)}
                        />
                    </FormGroup>
                }
                {
                    shapeApplicable && layerDef.headingFieldId &&
                    <FormGroup inline label={""}>
                        <div style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
                            <Switch minimal={true} checked={layerDef.config.useHeadingField} hideLabels inline onChange={() => this.onUseDirectionToggle()} />
                            <span>{$t("aligntodirection")}</span>
                        </div>
                    </FormGroup>
                }
                <FormGroup label={$t("opacity")} css={css`flex-direction: row; align-items: center;`}>
                    <Slider
                        marks={marks} max={100} min={0}
                        onChange={this.onOpacityChange}
                        step={2}
                        value={Math.round(layerDef.config.opacity)}
                        className={"noselect"}
                    />
                </FormGroup>
                {
                    sizeApplicable &&
                    <FormGroup label={$t("size")} css={css`flex-direction: row; align-items: center;`}>
                        <Slider
                            marks={marks} max={100} min={0}
                            onChange={this.onSizeChange}
                            step={2}
                            value={Math.round(layerDef.config.size)}
                            className={"noselect"}
                        />
                    </FormGroup>
                }
                {
                    sizeVaryApplicable &&
                    <FormGroup label={$t("varysize")} css={css`flex-direction: row; align-items: center;`}>
                        <div style={{display: "flex", flexDirection: "row"}}>
                            <Switch checked={layerDef.config.sizeDependsOnSizeField} hideLabels inline onChange={() => this.onSizeDependsToggle()} />
                            <DatasetFieldSelect
                                fields={numbericFields}
                                value={dataset.getFieldDefById(layerDef.config.sizeFieldId)}
                                onFieldSelect={this.onSizeFieldChange}
                                disabled={!layerDef.config.sizeDependsOnSizeField}
                                style={{width: "100%"}}
                            />
                        </div>
                    </FormGroup>
                }
            </FormContainer>
        );
    }

}
